
/**
 * System Message Slice - Manages system-wide notifications and alerts
 *
 * Features:
 * - Displays messages from CMS by location (homepage, map, etc.)
 * - Shows messages based on visibility and content only
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

export const LOADING_STATUSES = {
    idle: 'idle',
    loading: 'loading',
    succeeded: 'succeeded',
    failed: 'failed'
};

const initialState = {
    messages: [],
    platformNotices: {
        notices: [],
        status: LOADING_STATUSES.idle,
        lastUpdated: null
    }
};

export const fetchSystemNoticesOnce = createAsyncThunk(
    'systemMessages/fetchSystemNoticesOnce',
    async () => {
        const { CMSService } = await import('client/services');
        return await CMSService.getPlatformNotices();
    },
    {
        condition: (_, { getState }) => {
            const status = getState()?.systemMessages?.platformNotices?.status;
            return status === LOADING_STATUSES.idle;
        }
    }
);

// Slice for managing system messages state and actions
const systemMessageSlice = createSlice({
    name: 'systemMessages',
    initialState,
    reducers: {
        setMessages: (state, action) => { state.messages = action.payload; }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSystemNoticesOnce.pending, (state) => {
                state.platformNotices.status = LOADING_STATUSES.loading;
            })
            .addCase(fetchSystemNoticesOnce.fulfilled, (state, action) => {
                if (action.payload) {
                    state.platformNotices.notices = action.payload.notices || [];
                    state.platformNotices.lastUpdated = action.payload.lastUpdated || null;
                }
                state.platformNotices.status = LOADING_STATUSES.succeeded;
            })
            .addCase(fetchSystemNoticesOnce.rejected, (state) => {
                state.platformNotices.status = LOADING_STATUSES.failed;
            });
    }
});

// Extract action creators from the slice
const { setMessages } = systemMessageSlice.actions;

// helpers for valid messages
const hasValidContent = (msg) => {
    return msg.message && typeof msg.message === 'string' && msg.message.trim();
};

const isVisible = (msg) => {
    return ['undefined', 'true'].includes(String(msg.visible));
};

const hasValidTimeWindow = (msg) => {
    const { start, end } = msg;
    const now = new Date();
    if (start && new Date(start) > now) {
        return false; // Not active yet
    }
    if (end && new Date(end) < now) {
        return false; // Expired
    }
    return true; // Active
};

const selectActiveMessages = (state) => {
    if (!state.systemMessages?.messages) {
        return [];
    }

    return state.systemMessages.messages.filter(msg =>
        hasValidContent(msg) &&
        hasValidTimeWindow(msg) &&
        isVisible(msg)
    );
};

// Selector that filters active messages by specific location
// Messages without a location (null, undefined, or empty) are shown on all pages
const selectMessagesByLocation = (location) => (state) => {
    const activeMessages = selectActiveMessages(state);
    const locationMessages = activeMessages.filter(msg => {
        if (msg.location === location) {
            return true;
        }

        if (msg.location === 'all') {
            return true;
        }

        if (!msg.location || msg.location.trim() === '') {
            return true;
        }
        return false;
    });
    return locationMessages;
};

export const selectPlatformNotices = (state) => state.systemMessages.platformNotices.notices ?? [];
export const selectPlatformNoticesLastUpdated = (state) => state.systemMessages.platformNotices.lastUpdated;

export {
    setMessages,
    selectActiveMessages,
    selectMessagesByLocation,
    hasValidContent,
    isVisible,
    hasValidTimeWindow
};

export default systemMessageSlice.reducer;
